<template>
  <div>
    <div>
      <VModal
        ref="hint_modal"
        name="hint_modal"
        width="80%"
        height="90%"
        style="min-width: 800px; min-height: 600px;"
        @closed="handleHintModalClose"
      >
        <div style="position: absolute; top: 25px; right: 25px;">
          <button @click="$modal.hide('hint_modal')">
            &#10006;
          </button>
        </div>
        <div
          style="width: 100%; height: 100%;"
          class="text-center flex justify-center items-center"
        >
          <HintEdit
            v-if="editingHintId"
            :isRoot="false"
            :hintId="editingHintId"
            @HINT_UPDATED="afterHintUpdated"
          />
          <CreateHint v-else :isRoot="false" @HINT_CREATED="afterHintCreated" />
        </div>
      </VModal>
    </div>

    <div class="w-full px-5 m-auto">
      <div class="mb-16">
        <h2>
          {{ is_blockly ? "Blockly " : "Coding " }}
          {{ mode === "add" ? "Add" : "Edit" }}
        </h2>
      </div>
      <div class="flex items-center">
        <div class="flex items-center w-3/4">
          <label class="mr-8 font-bold text-lg">Name</label>
          <input v-model="codingObject.name" type="text" />
        </div>
        <div class="w-1/4">
          <div
            class="float-right 
              cursor-pointer rounded p-2 px-6 
              font-semibold text-white
              bg-blue
              hover:shadow-md"
            @click="spitCodeJSON"
          >
            Save
          </div>
        </div>
      </div>

      <div class="flex items-center w-3/4 mt-3">
        <label class="mr-10 font-bold text-lg">Tags</label>
        <div class="w-full">
          <VueSelect
            v-model="selectedTags"
            :options="tagOptions"
            label="name"
            multiple
          />
        </div>
      </div>

      <div class="flex items-center w-3/4 mt-3">
        <label class="mr-10 font-bold text-lg">Add Company</label>
        <div class="w-full">
          <VueSelect
            v-model="selectedCompany"
            :options="companyOptions"
            label="name"
            multiple
            :closeOnSelect="false"
          />
        </div>
      </div>

      <div class="flex items-center w-3/4 mt-3">
        <label class="mr-10 font-bold text-lg">Add Proficiency Level</label>
        <div class="w-full">
          <VueSelect
            v-model="codingObject.proficiency_level"
            :options="questionProficiencyOptions"
          />
        </div>
      </div>

      <div class="flex items-center w-3/4 mt-3">
        <label class="mr-10 font-bold text-lg">Question Bank Tags</label>
        <div class="w-full">
          <VueSelect
            v-model="selectedQuestionBankTags"
            :options="questionBankTagOptions"
            label="name"
            multiple
          />
        </div>
      </div>

      <div class="flex w-full mt-20 rounded-sm shadow-md">
        <div
          v-for="tab in [
            'Content',
            'Templates',
            'Test Cases',
            'Attachments',
            'Points & Hints',
          ]"
          :key="tab"
          class="w-1/4 text-center tab"
          :class="`${activeTab === tab ? 'tab-active' : ''}`"
          @click="activeTab = tab"
        >
          {{ `${tab} ${getTabStats(tab)}` }}
        </div>
      </div>

      <div class="p-5 mt-5 mb-24">
        <div v-if="activeTab === 'Content'">
          <div class="flex items-center">
            <label
              class="small-1 text-right font-bold text-md whitespace-no-wrap"
            >
              CMS ID
            </label>
            <input
              v-model.trim="codingObject.cms_id"
              class="small-8 ml-2"
              type="text"
              @blur="fetchCMSContent"
            />
          </div>

          <div v-if="codingObject.id" class="flex items-center mt-4">
            <label
              class="small-1 text-right font-bold text-md whitespace-no-wrap"
            >
              ID
            </label>
            <input
              class="small-4 ml-2"
              :value="codingObject.id"
              readonly="true"
              type="text"
            />
          </div>

          <div class="row mt-10">
            <div class="small-1 columns mt-1 font-bold text-md text-right">
              Content
            </div>
            <div class="small-5 columns ml-2">
              <MarkdownReadingEditor
                v-model="codingObject.content"
                @imageAdded="catchImageAddedEvent"
              />
            </div>
            <div class="small-5 columns">
              <div style="padding:16px;" class="markdown-body">
                <VueMarkdown :source="getRenderedMarkdownToHTML()" />
              </div>
            </div>
          </div>
        </div>

        <div v-if="activeTab === 'Templates'">
          <div class="row">
            <div class="flex items-center">
              <div class="mr-6 font-bold text-md">Template Folding</div>
              <div class="small-4">
                <VueSelect
                  :value="
                    templateFoldingState[
                      codingObject.template_code_folding_state
                    ] &&
                      templateFoldingState[
                        codingObject.template_code_folding_state
                      ].name
                  "
                  label="name"
                  :reduce="option => option.name"
                  :options="templateFoldingState"
                  @input="
                    option =>
                      (codingObject.template_code_folding_state =
                        option && option.value)
                  "
                />
              </div>
            </div>
          </div>

          <div class="row">
            <div
              v-for="(item, i) in languageArray"
              :key="i"
              class="small-12 columns bg-grey-lighter rounded px-6 mt-5 hover:bg-grey-light"
            >
              <LanguageItem
                :languages="languages"
                :index="i"
                :item="item"
                :is_blockly="is_blockly"
                @OPEN_MODAL_CONTENT="toggleModal"
                @SET_BLOCKLY_TOOLBOX="setBlocklyToolbox"
                @SET_BLOCKLY_WORKSPACE="setBlocklyWorkspace"
              />
              <div
                class="remove-language-class"
                @click="removeLanguageArray(i)"
              >
                - remove language
              </div>
            </div>
          </div>
          <button
            class="mt-6 border bg-grey float-right px-4 py-4 rounded"
            @click="addToLanguageArray"
          >
            + Add Language
          </button>
        </div>

        <div v-if="activeTab === 'Test Cases'">
          <h2>Small Test Cases</h2>
          <div class="test-cases-container">
            <div class="test_case border-dashed border border-2 p-6">
              <div class="font-bold text-lg flex justify-between items-end">
                <span>Small Test Case</span>
              </div>

              <div class="form_group">
                <div class="form_field">
                  <input
                    id="display_small_input"
                    v-model="codingObject.display_test_case_input"
                    type="checkbox"
                  />
                  <label for="display_small_input">Show Input</label>
                </div>
              </div>

              <div class="test_case_file_uploads">
                <div class="file_upload">
                  <div class="mb-1">Small Input</div>
                  <CodeSingleFileUpload
                    :maxFileSize="maxFileSize"
                    @codingFileUploadFail="handleFileUploadFailure"
                    @codingFileUploadSuccess="handleSmallInEvent($event)"
                  />
                  <div
                    v-if="
                      codingObject.test_case_file_input_id &&
                        codingObject.test_case_file_input_id.length > 2
                    "
                    class="file_id"
                    @click="toggleModal(codingObject.test_case_file_input_id)"
                  >
                    <strong>File ID:</strong>
                    <span class="link">{{
                      codingObject.test_case_file_input_id
                    }}</span>
                  </div>
                </div>
                <div class="file_upload">
                  <div class="mb-1">Small Output</div>
                  <CodeSingleFileUpload
                    :maxFileSize="maxFileSize"
                    @codingFileUploadFail="handleFileUploadFailure"
                    @codingFileUploadSuccess="handleSmallOutEvent($event)"
                  />
                  <div
                    v-if="
                      codingObject.test_case_file_output_id &&
                        codingObject.test_case_file_output_id.length > 2
                    "
                    class="file_id"
                    @click="toggleModal(codingObject.test_case_file_output_id)"
                  >
                    <strong>File ID: </strong>
                    <span class="link">{{
                      codingObject.test_case_file_output_id
                    }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <hr class="border border-bottom-1 border-grey-lighter my-12" />

          <h2>Large Test Cases</h2>
          <div class="test-cases-container">
            <TestCaseItem
              v-for="(item, i) in testCaseArray"
              :key="i"
              :item="item"
              :index="i"
              @OPEN_MODAL_CONTENT="toggleModal"
              @REMOVE_TEST_CASE="removeTestCase(i)"
            />
          </div>
          <div style="display: grid; place-content: center">
            <button
              class="border border-1 border-grey hover:bg-blue rounded-lg bg-grey-lightest px-6
                focus:bg-blue focus:border-blue-darkest py-4 rounded-sm"
              @click="addTestCase"
            >
              + Add Test Case
            </button>
          </div>
        </div>

        <div v-if="activeTab === 'Attachments'">
          <div
            v-for="(item, idx) in codingObject.files"
            :key="`${item.id}_${idx}`"
          >
            <AttachmentItem
              :attachment="item"
              :itemIndex="idx"
              @OPEN_S3_UPLOAD_WIDGET="openS3UploadWidget"
              @REMOVE_ATTACHMENT="removeAttachment"
              @CAN_DOWNLOAD_CLICKED="handleCanDownloadClick"
            />
          </div>
          <button
            class="mt-6 border bg-grey float-right px-4 py-4 rounded-sm"
            @click="addAttachment"
          >
            + Add Attachment
          </button>
          <AWSS3UploadWidget
            v-if="showS3UploadWidget"
            :serverUrl="serverUrl"
            :dropZoneTitle="'Drag and drop to upload your file'"
            :category="'dev-attachment-upload'"
            @CLOSE_BUTTON_CLICK="showS3UploadWidget = !showS3UploadWidget"
            @FILE_ADDED="handleFileAddition"
            @FILE_UPLOAD_SUCCESS="handleUploadedFileToS3"
          />
        </div>
        <div v-if="activeTab === 'Points & Hints'">
          <div>
            <div class="w-full flex">
              <div class="w-1/2">
                <div class="flex items-center relative">
                  <label
                    class="text-right font-bold text-md whitespace-no-wrap"
                  >
                    Total Points
                  </label>
                  <div class="flex flex-col">
                    <input
                      v-model="codingObject.points"
                      class="small-4 ml-2"
                      type="text"
                    />
                    <small class="text-red absolute" style="bottom: -35px"
                      >* If you modify total points, please refresh user path
                      post saving to get it reflected there.</small
                    >
                  </div>
                </div>
              </div>
              <div class="w-1/2">
                <div class="flex items-center">
                  <label class="text-red font-bold text-md"
                    >Penalty Points</label
                  >
                  <div
                    class="rounded-full px-3 py-2 ml-2 bg-grey-light flex items-center justify-center"
                  >
                    <h4>
                      {{ totalHintsSum + totalEditorialSum }} /
                      {{ codingObject.points }}
                    </h4>
                  </div>
                </div>
              </div>
            </div>

            <div class="w-full flex mt-8">
              <div class="w-1/2">
                <div class="font-bold text-md ml-1">Hints</div>
                <div class="bg-grey-lighter rounded p-6 mt-2 font-bold text-md">
                  <div
                    v-for="(hint, idx) in selectedHints"
                    :key="idx"
                    class="flex justify-center items-center mb-3"
                  >
                    <div class="bg-white rounded w-3/4">
                      <VueSelect
                        :ref="hint.id"
                        :value="hint.name"
                        :options="hintOptions"
                        label="name"
                        :reduce="hint => hint.name"
                        @search:blur="handleBlur"
                        @input="option => hintSelection(option, idx, hint.id)"
                      />
                    </div>
                    <span class="ml-2 text-sm">{{
                      hint.points ? `${hint.points} Points` : ""
                    }}</span>

                    <span
                      class="text-sm ml-auto cursor-pointer"
                      @click="e => handleUpdateHint(e, idx, hint)"
                    >
                      <img
                        class="edit_icon"
                        src="../../../../public/images/icons/your_answer.svg"
                        alt=""
                      />
                    </span>

                    <span
                      class="text-sm ml-auto cursor-pointer"
                      @click="e => removeHint(e, idx, hint)"
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        class="remove_icon"
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                      >
                        <path
                          d="M18.5 15c-2.484 0-4.5 2.015-4.5 4.5s2.016 4.5 4.5 4.5c2.482 0 4.5-2.015 4.5-4.5s-2.018-4.5-4.5-4.5zm2.5 5h-5v-1h5v1zm-5-11v4.501c-.748.313-1.424.765-2 1.319v-5.82c0-.552.447-1 1-1s1 .448 1 1zm-4 0v10c0 .552-.447 1-1 1s-1-.448-1-1v-10c0-.552.447-1 1-1s1 .448 1 1zm1.82 15h-11.82v-18h2v16h8.502c.312.749.765 1.424 1.318 2zm-6.82-16c.553 0 1 .448 1 1v10c0 .552-.447 1-1 1s-1-.448-1-1v-10c0-.552.447-1 1-1zm14-4h-20v-2h5.711c.9 0 1.631-1.099 1.631-2h5.316c0 .901.73 2 1.631 2h5.711v2zm-1 2v7.182c-.482-.115-.983-.182-1.5-.182l-.5.025v-7.025h2z"
                        />
                      </svg>
                    </span>
                  </div>

                  <div class="row mt-5">
                    <button
                      class="rounded px-3 py-2 bg-grey hover:bg-grey-dark hover:text-white cursor-pointer float-right"
                      :title="
                        hintOptions.length === 0
                          ? 'No hint found to add'
                          : 'Add New Hint'
                      "
                      :disabled="hintOptions.length === 0"
                      @click="addNewHint"
                    >
                      + Add Hint
                    </button>
                  </div>

                  <div class="row mt-5">
                    <button
                      class="rounded px-3 py-2 bg-grey hover:bg-grey-dark hover:text-white cursor-pointer float-right"
                      :title="'Create and Add a new Hint'"
                      @click="handleCreateHint"
                    >
                      + Create New Hint
                    </button>
                  </div>
                </div>
              </div>

              <div class="w-1/2 ml-4">
                <div class="font-bold text-md ml-1">Editorial</div>
                <div class="bg-grey-lighter rounded p-6 mt-2 font-bold text-lg">
                  <div>
                    <div
                      v-if="selectedEditorial"
                      class="flex justify-center items-center mb-3"
                    >
                      <div class="bg-white rounded w-3/4">
                        <VueSelect
                          ref="editorialSelector"
                          :value="selectedEditorial"
                          :options="editorialOption"
                          label="name"
                          :reduce="editorial => editorial.name"
                          @search:blur="handleEditorialSelectionBlur"
                          @input="
                            option =>
                              (selectedEditorial = option ? option : undefined)
                          "
                        />
                      </div>
                      <span class="ml-2 text-sm">{{
                        totalEditorialSum ? `${totalEditorialSum} Points` : ""
                      }}</span>
                      <span
                        class="text-sm ml-auto cursor-pointer"
                        @click="e => (selectedEditorial = null)"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          class="remove_icon"
                          width="14"
                          height="14"
                          viewBox="0 0 24 24"
                        >
                          <path
                            d="M18.5 15c-2.484 0-4.5 2.015-4.5 4.5s2.016 4.5 4.5 4.5c2.482 0 4.5-2.015 4.5-4.5s-2.018-4.5-4.5-4.5zm2.5 5h-5v-1h5v1zm-5-11v4.501c-.748.313-1.424.765-2 1.319v-5.82c0-.552.447-1 1-1s1 .448 1 1zm-4 0v10c0 .552-.447 1-1 1s-1-.448-1-1v-10c0-.552.447-1 1-1s1 .448 1 1zm1.82 15h-11.82v-18h2v16h8.502c.312.749.765 1.424 1.318 2zm-6.82-16c.553 0 1 .448 1 1v10c0 .552-.447 1-1 1s-1-.448-1-1v-10c0-.552.447-1 1-1zm14-4h-20v-2h5.711c.9 0 1.631-1.099 1.631-2h5.316c0 .901.73 2 1.631 2h5.711v2zm-1 2v7.182c-.482-.115-.983-.182-1.5-.182l-.5.025v-7.025h2z"
                          />
                        </svg>
                      </span>
                    </div>

                    <div v-else class="row mt-5">
                      <div
                        class="rounded px-3 py-2 bg-grey hover:bg-grey-dark hover:text-white cursor-pointer float-right"
                        @click="addEditorial"
                      >
                        + Add Editorial
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div>
        <SweetModal
          ref="rawcontentmodal"
          width="80%"
          @closed="modalOpen = false"
        >
          <pre>{{ modalContent }}</pre>
        </SweetModal>
      </div>
    </div>
  </div>
</template>

<script>
/**
 * This is a new component that takes care of converting the coding question object to UI format and back
 * again. The other two components you see... CodingAdd, CodingEdit are legacy components,
 * preserved for the sake of reference
 */
import { SweetModal } from "sweet-modal-vue"
import VueSelect from "vue-select"
import VueMarkdown from "vue-markdown"
import AWSS3UploadWidget from "@edyst/shared-components/src/components/AWSS3UploadWidget"
import markdownHelpers from "@/utils/markdownEditorHelpers"
import MarkdownReadingEditor from "@/components/questions/MarkdownReadingEditor"
import CodeSingleFileUpload from "@/components/shared/fileUpload/SingleFileUpload"
import idGenerator from "@/utils/idGenerator"
import TestCaseItem from "./TestCaseItem"
import LanguageItem from "./LanguageItem"

import siteConfig from "@/utils/siteConfig"
import { hasKey } from "@/utils/objectUtil"
import AttachmentItem from "@/components/coding/CodeComponents/AttachmentItem"
import CreateHint from "@/components/coding/CreateHint"
import HintEdit from "@/components/coding/HintEdit"

export default {
  components: {
    VueMarkdown,
    LanguageItem,
    TestCaseItem,
    CodeSingleFileUpload,
    VueSelect,
    MarkdownReadingEditor,
    SweetModal,
    AWSS3UploadWidget,
    AttachmentItem,
    CreateHint,
    HintEdit,
  },
  props: ["codingObject", "mode", "is_blockly"],
  data() {
    return {
      activeTab: "Content",
      serverUrl: `${siteConfig.siteURL}uploads/public`,
      showS3UploadWidget: false,
      currentAttachmentFileIndex: 0,
      templateFoldingState: [
        { value: 0, name: "0 Disabled" },
        { value: 1, name: "1 Enabled" },
        { value: 2, name: "2 Expand" },
      ],
      languageArray: [],
      testCaseArray: [],
      languages: [],
      tagOptions: [],
      selectedTags: [],
      editorialOption: [],
      selectedEditorial: undefined,
      hintOptions: [],
      allHints: [],
      selectedHints: [],
      markdownRenderer: undefined,
      // nginx default POST client payload size is 1MB
      // we are limiting upload to about 800KB here
      maxFileSize: 800 * 1024,
      modalOpen: false,
      modalContent: "",
      blockly: [],
      uploadedFileName: "",
      editingHintId: "",
      companyOptions: [],
      selectedCompany: [],
      questionProficiencyOptions: ["easy", "medium", "hard"],
      questionProficiency: undefined,
      questionBankTagOptions: [],
      selectedQuestionBankTags: [],
    }
  },
  computed: {
    totalPoints() {
      let total = 0
      this.testCaseArray
        .filter(tc => tc.points)
        .forEach(tc => {
          total += tc.points
        })
      return total
    },
    totalHintsSum() {
      let hintSum = 0
      if (this.selectedHints) {
        for (let i = 0; i < this.selectedHints.length; i++) {
          if (this.selectedHints[i].points) {
            hintSum += this.selectedHints[i].points
          }
        }
      }
      return hintSum
    },
    totalEditorialSum() {
      let editorialSum = 0
      if (this.selectedEditorial) {
        if (this.selectedEditorial.points) {
          editorialSum += this.selectedEditorial.points
        }
      }
      return editorialSum
    },
  },
  watch: {
    activeTab(newVal) {
      if (newVal === "Points & Hints") {
        this.populateHints()
      }
    },
  },
  created() {
    //
    this.selectedHints =
      this.codingObject.data &&
      this.codingObject.data.hints &&
      this.codingObject.data.hints.length
        ? [...this.codingObject.data.hints]
        : []
    this.selectedEditorial =
      this.codingObject.data &&
      this.codingObject.data.editorial &&
      this.codingObject.data.editorial.id
        ? { ...this.codingObject.data.editorial }
        : undefined
    this.markdownRenderer = window.markdownit()
    this.getLanguages()
    this.populateTags()
    this.populateHints()
    this.populateEditorial()

    this.populateLanguageArray()
    this.populateTestCaseArray()

    this.populateCompany()
    this.populateQuestionBankTags()
  },
  methods: {
    handleCanDownloadClick(data) {
      this.$emit("CAN_DOWNLOAD_CLICKED", data)
    },
    removeAttachment(index) {
      this.$emit("REMOVE_ATTACHMENT", index)
    },
    addAttachment() {
      this.$emit("ADD_NEW_ATTACHMENT")
    },
    openS3UploadWidget(idx) {
      this.currentAttachmentFileIndex = idx
      this.showS3UploadWidget = true
    },
    handleFileAddition(file) {
      this.uploadedFileName = file.name
    },
    handleUploadedFileToS3(imageUrl) {
      if (!this.uploadedFileName || !imageUrl) return
      const data = {
        name: this.uploadedFileName,
        public_url: imageUrl,
        can_download: this.codingObject.files[this.currentAttachmentFileIndex]
          .can_download,
      }
      this.axios({
        method: "post",
        url: `${siteConfig.siteURL}uploads/`,
        data,
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "x-access-token": localStorage.token,
        },
      }).then(res => {
        if (res.data.status === true) {
          this.showS3UploadWidget = false
          this.$emit("HANDLE_UPLOADED_ATTACHMENT_FILE", {
            fileId: res.data.file.id,
            name: this.uploadedFileName,
            index: this.currentAttachmentFileIndex,
            public_url: imageUrl,
          })
        }
      })
    },
    getTabStats(tab) {
      switch (tab) {
        case "Templates": {
          return `(${this.languageArray.length} Langs)`
        }
        case "Test Cases": {
          return `(1 + ${this.testCaseArray.length})`
        }
        case "Points & Hints": {
          const hints = this.selectedHints.filter(hint => hint.id)
          const editorial =
            this.selectedEditorial && this.selectedEditorial.id ? 1 : 0
          return `(${hints.length + editorial})`
        }
        case "Attachments": {
          return `(${this.codingObject.files.length})`
        }
        default:
          return ""
      }
    },
    fetchCMSContent() {
      const { cms_id } = this.codingObject
      if (cms_id && cms_id.length) {
        this.axios({
          method: "get",
          url: `${siteConfig.ghostURL}ghost/api/v2/content/posts/${cms_id}/?key=${siteConfig.ghostKey}`,
        }).then(res => {
          this.codingObject.content = res.data.posts
            ? res.data.posts[0].html
            : ""
        })
      }
    },
    addNewHint() {
      if (this.hintOptions.length > 0) {
        // remove empty select box, only allow one empty select box
        this.cleanUpEmptyHintOptions()

        this.selectedHints.push({
          name: undefined,
          id: "tempHintId", // used to focus once added
          points: undefined,
        })

        this.$nextTick(() => {
          /* focus newly added select box */
          this.$refs.tempHintId[0].$refs.search.focus()
        })
      } else {
        this.$noty.error("No hint found to add")
      }
    },
    handleCreateHint() {
      // on create button click
      this.$modal.show("hint_modal")
    },
    afterHintCreated(data) {
      // After the hint is created
      const createdHint = {
        name: data.hintName,
        id: data.hintId,
        points: Number(data.hintPoints),
      }
      this.allHints.push(createdHint)
      this.selectedHints.push(createdHint)
      this.cleanUpEmptyHintOptions()
      this.$modal.hide("hint_modal")
    },
    handleUpdateHint(e, idx, hint) {
      // edit hint btn onclick handler
      this.editingHintId = hint.id
      this.editingHintIdx = idx
      this.$modal.show("hint_modal")
    },
    afterHintUpdated(data) {
      // After a hint is updated, update the hint options.
      const updatedHintIdx = this.selectedHints.findIndex(
        h => h.id === data.hintId
      )
      const updatedHint = {
        name: data.hintName,
        id: data.hintId,
        points: Number(data.hintPoints),
      }
      this.$set(this.selectedHints, updatedHintIdx, updatedHint)
      this.$modal.hide("hint_modal")
    },
    handleHintModalClose() {
      // on closing the hint create/edit modal
      this.editingHintId = ""
    },
    addEditorial() {
      this.selectedEditorial = {
        name: undefined,
        points: undefined,
        id: undefined,
      }
      this.$nextTick(() => {
        this.$refs.editorialSelector.$refs.search.focus()
      })
    },
    hintSelection(option, idx, hintId) {
      /* remove any hint that doesn't have complete data, also force admin
      to add new empty select box each time he wants to add new hint */
      this.cleanUpEmptyHintOptions()
      if (option) {
        this.hintOptions = this.hintOptions.filter(
          hint => hint.id !== option.id
        )
        this.selectedHints[idx] = option
      } else {
        /* store hint back to option list */
        const removedHint = this.selectedHints.find(h => h.id === hintId)
        this.hintOptions.push(removedHint)
        /* removed hint */
        this.selectedHints = this.selectedHints.filter(h => h.id !== hintId)
      }
    },
    removeHint(e, idx, hint) {
      const hints = this.selectedHints.filter((h, i) => i !== idx)
      if (hint.id) {
        this.hintOptions.push(hint)
      }
      this.selectedHints = [...hints]
    },
    handleBlur() {
      this.cleanUpEmptyHintOptions()
    },
    handleEditorialSelectionBlur() {
      if (
        !this.selectedEditorial.name &&
        !this.selectedEditorial.points &&
        !this.selectedEditorial.id
      ) {
        this.selectedEditorial = null
      }
    },
    cleanUpEmptyHintOptions() {
      this.selectedHints = this.selectedHints.filter(
        h => h.id && h.name && h.points
      )
    },
    setBlocklyToolbox(payload) {
      const {
        idx,
        event: {
          target: { value },
        },
      } = payload
      this.languageArray[idx].blockly_toolbox = value
    },
    setBlocklyWorkspace(payload) {
      const {
        idx,
        event: {
          target: { value },
        },
      } = payload
      this.languageArray[idx].blockly_workspace = value
    },
    toggleModal(id) {
      const loader = this.$loading.show()
      this.axios({
        method: "get",
        url: `${siteConfig.siteURL}uploads/${id}`,
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "x-access-token": localStorage.token,
        },
      })
        .then(res => {
          const url = res.data.data.public_url

          this.axios({
            method: "get",
            url,
          })
            // eslint-disable-next-line no-shadow
            .then(res => {
              this.modalContent = res.data
              if (this.modalOpen) {
                this.$refs.rawcontentmodal.close()
              } else {
                this.$refs.rawcontentmodal.open()
              }
              loader.hide()
            })
            .catch(() => {
              loader.hide()
            })
        })
        .catch(() => {
          loader.hide()
        })
    },

    validationCheck() {
      if (this.codingObject.files && this.codingObject.files.length) {
        if (
          this.codingObject.files.every(
            item =>
              item.id &&
              typeof item.id === "string" &&
              item.name &&
              typeof item.name === "string"
          )
        ) {
          return true
        }
        this.$noty.error(
          "File not uploaded for one or more attachments, please check and try again."
        )
        return false
      }
      if (
        this.totalHintsSum + this.totalEditorialSum >
        this.codingObject.points
      ) {
        this.$noty.warning(
          "penality points are exceeded to the question points"
        )
        return false
      }
      if (this.codingObject.name.length < 2) {
        this.$noty.error("Please choose a valid name")
        return false
      }
      if (this.codingObject.name.points < 0) {
        this.$noty.error("Please enter valid points")
        return false
      }
      if (this.codingObject.content.length < 2) {
        this.$noty.error("Please try again after entering some content")
        return false
      }

      if (this.languageArray.length === 0) {
        this.$noty.error("Please add atleast one language section")
        return false
      }

      for (let i = 0; i < this.languageArray.length; i++) {
        if (!this.languageArray[i].language) {
          this.$noty.error(
            "please choose a valid language in languages section"
          )
          return false
        }
        if (this.languageArray[i].template.length < 7) {
          this.$noty.error(
            "please upload a valid template file in languages section"
          )
          return false
        }
      }

      if (this.totalPoints > this.codingObject.points) {
        this.$noty.error("Test Cases sum is exceeds to it's question Points")
        return false
      }

      // For SQL questions, test case (Run) files are optional
      let allow_no_test_case_files = false
      const codeTemplate = this.codingObject.code_template_file_json.data
      for (let j = 0; j < codeTemplate.length; j++) {
        if (codeTemplate[j].language.startsWith("SQL")) {
          allow_no_test_case_files = true
        }
      }

      if (!allow_no_test_case_files) {
        if (this.codingObject.test_case_file_input_id.length < 7) {
          this.$noty.error("please choose a test case input file")
          return false
        }
        if (this.codingObject.test_case_file_output_id.length < 7) {
          this.$noty.error("please choose a test case output file")
          return false
        }
      }

      for (let i = 0; i < this.testCaseArray.length; i++) {
        if (
          !this.testCaseArray[i].large_in ||
          this.testCaseArray[i].large_in.length < 7
        ) {
          this.$noty.error(`please add a valid large IN file at : #${i + 1}`)
          return false
        }
        if (
          !this.testCaseArray[i].large_out ||
          this.testCaseArray[i].large_out.length < 7
        ) {
          this.$noty.error(`please add a valid large OUT file at : #${i + 1}`)
          return false
        }
        /* if test case has points but is_required not present, throw error */
        if (
          this.testCaseArray[i].points &&
          this.testCaseArray[i].points > 0 &&
          !hasKey(this.testCaseArray[i], "is_required")
        ) {
          this.$noty.error(
            `Test case has points but required status not present in ${this.testCaseArray[i].title}`
          )
          return false
        }
        if (
          // if test case's points is 0 then remove is_required key
          (!this.testCaseArray[i].points ||
            this.testCaseArray[i].points === 0) &&
          hasKey(this.testCaseArray[i], "is_required")
        ) {
          delete this.testCaseArray[i].is_required
        }
      }

      if (!this.isRequiredKeyStatusCheck()) {
        this.$noty.error(
          "Required status is present for one or more test cases, please add explicit value for all test cases"
        )
        return false
      }

      return true
    },
    isRequiredKeyStatusCheck() {
      /* either all test cases must have is_required key or none, mix isn't allowed */
      if (this.testCaseArray.every(t => hasKey(t, "is_required"))) return true // is_required key present for all
      if (this.testCaseArray.every(t => !hasKey(t, "is_required"))) return true // is_required key not present for all
      return false // err: mix value
    },
    populateTestCaseArray() {
      const arr = this.codingObject.submission_cases
      if (JSON.stringify(arr) === "{}") {
        this.testCaseArray = []
        return
      }
      this.testCaseArray = this.codingObject.submission_cases
    },
    populateLanguageArray() {
      const placeholderJSON = this.codingObject.placeholder_json.data

      for (let i = 0; i < placeholderJSON.length; i++) {
        const codeTemplate = this.codingObject.code_template_file_json.data
        for (let j = 0; j < codeTemplate.length; j++) {
          if (placeholderJSON[i].language === codeTemplate[j].language) {
            this.languageArray.push({
              language: placeholderJSON[i].language,
              template: codeTemplate[j].code_template_file_id,
              placeholder: placeholderJSON[i].placeholder,
              blockly_toolbox: placeholderJSON[i].blockly_toolbox,
              blockly_workspace: placeholderJSON[i].blockly_workspace,
            })
          }
        }
      }
    },
    spitCodeJSON() {
      this.codingObject.data = this.getHintAndEditorialsAdded()
      this.codingObject.code_template_file_json.data = this.getCodeTemplateFileJSONFormat()

      if (!this.validationCheck()) {
        return
      }
      this.codingObject.ops_qtags_unlink = this.getDeletedTags()
      this.codingObject.ops_qtags = this.getAddedTags()

      this.codingObject.companies = this.selectedCompany.map(item => item.id)
      this.codingObject.proficiency_level = this.codingObject.proficiency_level
      this.codingObject.question_bank_tags = this.selectedQuestionBankTags.map(
        item => item.id
      )

      /**
       * Rewrite the titles, just incase the middle elements have been deleted and the order is lost.
       */

      for (let i = 0; i < this.testCaseArray.length; i++) {
        this.testCaseArray[i].title = `Test Case ${i + 1}`
      }

      this.codingObject.submission_cases = this.testCaseArray
      this.codingObject.placeholder_json.data = this.getPlaceholderJSONFormat()

      this.codingObject.template_code_folding_state = this.codingObject
        .template_code_folding_state
        ? parseInt(this.codingObject.template_code_folding_state)
        : 0

      this.$emit("CODING_OBJECT_EMITTED", this.codingObject)
    },
    populateHints() {
      const loader = this.$loading.show()
      this.axios({
        method: "get",
        url: `${siteConfig.siteURL}admin/questions?pagination=false&search_by_type=10&fields=id,name,points`,
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "x-access-token": localStorage.token,
        },
      })
        .then(res => {
          loader.hide()
          this.allHints = res.data.items
          const addedHints = [
            ...new Set(this.selectedHints.map(item => item.id)),
          ]
          this.hintOptions = this.allHints.filter(
            hint => !addedHints.includes(hint.id)
          )
        })
        .catch(error => {
          loader.hide()
          this.$noty.error(`Failed to get the hint questions${error}`)
        })
    },
    populateEditorial() {
      this.axios({
        method: "get",
        url: `${siteConfig.siteURL}admin/questions?pagination=false&search_by_type=11&fields=id,name,points`,
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "x-access-token": localStorage.token,
        },
      })
        .then(res => {
          this.editorialOption = res.data.items
        })
        .catch(error => {
          this.$noty.error(`Failed to get the editorial questions${error}`)
        })
    },
    getHintAndEditorialsAdded() {
      let hints
      if (this.selectedHints) {
        hints = this.selectedHints.map(item => ({
          id: item.id,
          name: item.name,
          points: item.points,
        }))
      }
      const addHintAndEditorial = {}
      if (hints && hints.length > 0) {
        addHintAndEditorial.hints = hints
      }
      if (this.selectedEditorial && this.selectedEditorial.name) {
        addHintAndEditorial.editorial = this.selectedEditorial
      }
      // FE expects this to be null for no hints and editorial
      return Object.keys(addHintAndEditorial).length > 0
        ? addHintAndEditorial
        : null
    },
    getCodeTemplateFileJSONFormat() {
      const arr = []
      for (let i = 0; i < this.languageArray.length; i++) {
        const obj = {}

        obj.language = this.languageArray[i].language
        obj.code_template_file_id = this.languageArray[i].template
        arr.push(obj)
      }
      return arr
    },
    getPlaceholderJSONFormat() {
      const arr = []
      for (let i = 0; i < this.languageArray.length; i++) {
        const obj = {}

        obj.jz_code = this.getJZCode(this.languageArray[i].language)
        obj.language = this.languageArray[i].language
        obj.placeholder = this.languageArray[i].placeholder
        obj.blockly_toolbox = this.languageArray[i].blockly_toolbox
        obj.blockly_workspace = this.languageArray[i].blockly_workspace

        arr.push(obj)
      }

      return arr
    },

    getJZCode(language) {
      for (let i = 0; i < this.languages.length; i++) {
        if (this.languages[i].name === language) {
          return this.languages[i].jz_code
        }
      }
      throw new Error("no valid jzcode")
    },
    populateTags() {
      this.axios({
        method: "get",
        url: `${siteConfig.siteURL}qtags/?pagination=false`,
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "x-access-token": localStorage.token,
        },
      }).then(res => {
        const tags = res.data.items
        this.tagOptions = tags.map(item => ({
          id: item.id,
          name: item.name,
        }))
      })

      this.selectedTags = this.codingObject.qtags.map(item => ({
        id: item.id,
        name: item.name,
      }))
    },
    handleFileUploadFailure(message) {
      alert(`File upload failed! ${message}`)
    },
    handleSmallInEvent(event) {
      console.log(event)
      this.codingObject.test_case_file_input_id = event.id
    },
    handleSmallOutEvent(event) {
      this.codingObject.test_case_file_output_id = event.id
    },
    addTestCase() {
      this.testCaseArray.push({
        large_in: undefined,
        large_out: undefined,
        title: `Test Case ${this.testCaseArray.length + 1}`,
        id: idGenerator.generatePushID(),
        visible: false,
        points: undefined,
        is_required: true, // by default set it to true
      })
    },
    removeTestCase(i) {
      this.testCaseArray.splice(i, 1)
    },

    addToLanguageArray() {
      if (this.is_blockly) {
        this.languageArray.push({
          language: "",
          template: "",
          placeholder: "",
          blockly_toolbox: "",
          blockly_workspace: "",
        })
      } else {
        this.languageArray.push({
          language: "",
          template: "",
          placeholder: "",
        })
      }
    },
    removeLanguageArray(i) {
      this.languageArray.splice(i, 1)
    },
    getLanguages() {
      this.axios({
        method: "get",
        url: `${siteConfig.siteURL}languages/?pagination=false`,
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "x-access-token": localStorage.token,
        },
      })
        .then(res => {
          this.languages = res.data.items
        })
        .catch(err => err)
    },
    getDeletedTags() {
      const deletedTags = []
      for (let i = 0; i < this.codingObject.qtags.length; i++) {
        if (
          !this.findElementInArray(
            this.codingObject.qtags[i].name,
            this.selectedTags.map(item => item.name)
          )
        ) {
          deletedTags.push(this.codingObject.qtags[i])
        }
      }
      const finalTags = []
      for (let i = 0; i < deletedTags.length; i++) {
        finalTags.push(deletedTags[i].id)
      }
      return finalTags
    },
    getAddedTags() {
      const addedTags = []

      for (let i = 0; i < this.selectedTags.length; i++) {
        if (
          !this.findElementInArray(
            this.selectedTags[i].name,
            this.codingObject.qtags.map(item => item.name)
          )
        ) {
          addedTags.push(this.selectedTags[i])
        }
      }
      const finalTags = []
      for (let i = 0; i < addedTags.length; i++) {
        finalTags.push(addedTags[i].id)
      }
      return finalTags
    },
    findElementInArray(element, array) {
      for (let i = 0; i < array.length; i++) {
        if (array[i] === element) {
          return true
        }
      }
      return false
    },
    getRenderedMarkdownToHTML() {
      return this.markdownRenderer.render(
        markdownHelpers.decodeIDsToMarkdownLinks(
          this.codingObject.content,
          this.codingObject.attachments
        )
      )
    },
    catchImageAddedEvent(eventObj) {
      this.content = eventObj.encodedString
      for (let i = 0; i < eventObj.attachments.length; i++) {
        this.codingObject.attachments.push(eventObj.attachments[i])
      }
    },
    populateCompany() {
      this.axios({
        method: "get",
        url: `${siteConfig.siteURL}admin/company?pagination=false`,
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "x-access-token": localStorage.token,
        },
      }).then(res => {
        const companies = res.data.items
        this.companyOptions = companies.map(item => ({
          id: item.id,
          name: item.name,
        }))
      })
      if (this.codingObject.company_questions) {
        this.selectedCompany = this.codingObject.company_questions.map(
          item => ({
            id: item.company.id,
            name: item.company.name,
          })
        )
      }
    },

    populateQuestionBankTags() {
      this.axios({
        method: "get",
        url: `${siteConfig.siteURL}question_bank/tags?pagination=false`,
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          "x-access-token": localStorage.token,
        },
      }).then(res => {
        const tags = res.data.items
        this.questionBankTagOptions = tags.map(item => ({
          id: item.id,
          name: item.name,
        }))
      })

      if (this.codingObject.question_bank_tags) {
        this.selectedQuestionBankTags = this.codingObject.question_bank_tags.map(
          item => ({
            id: item.tag.id,
            name: item.tag.name,
          })
        )
      }
    },
  },
}
</script>

<style>
.add-language-class {
  margin: 20px;
  color: green;
  text-align: left;
  cursor: pointer;
}

.remove-language-class {
  margin: 20px;
  color: red;
  text-align: right;
  cursor: pointer;
}

.coding-label {
  padding-right: 40px;
  font-weight: 500;
  line-height: 47px;
  text-align: right;
}

.coding-row {
  margin-top: 20px;
  margin-bottom: 20px;
}

div > strong {
  color: blue;
}
.small-test-cases-container {
  padding-top: 60px;
  padding-bottom: 60px;
  border: 1px solid grey;
  background-color: #f9f9f9;
}

.test-cases-container {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(400px, 1fr));
  gap: 2rem;
  margin: 2rem auto;
}

.row .edyst-blockly-xml {
  width: 45%;
  height: 300px;
  margin-bottom: 50px;
}

.edyst-blockly-xml span {
  padding: 10px;
}
.edyst-blockly-xml textarea {
  overflow: auto;
  width: 100%;
  height: 100%;
  padding-top: 10px;
  padding-left: 10px;
  border: 1px solid #cccccc;
  border-radius: 5px;
  resize: none;
}

input {
  height: 34px;
  padding-left: 10px;
  border-radius: 4px;
}

.tab {
  padding: 10px;
  border: 1px solid #cccccc;
  cursor: pointer;
}
.tab:first-child {
  border-top-left-radius: 4px;
  border-bottom-left-radius: 4px;
}
.tab:last-child {
  border-top-right-radius: 4px;
  border-bottom-right-radius: 4px;
}
.tab-active {
  color: #ffffff;
  background-color: #3490dc;
}
.save-btn {
  float: right;
  padding: 7px;
  border-radius: 4px;
  cursor: pointer;
}
.remove_icon:hover {
  fill: red;
}
.edit_icon:hover {
  fill: orange;
}
.edit_icon {
  width: 16px;
  height: 16px;
}
.hints-editor-container {
  padding: 45px;
}
.markdown-preview {
  overflow-y: auto;
  height: 350px;
}
</style>

<style lang="postcss" scoped>
.test_case {
  display: grid;
  border-radius: 0.5rem;
  background: #fdfdfd;
  gap: 2.5rem;
}

.test_case:hover {
  border-color: rgba(0, 0, 0, 0.4);
  background: rgba(0, 0, 0, 0.02);
}

.form_group {
  display: flex;
  max-width: 50ch;
  gap: 1rem;
  justify-content: space-between;
  align-items: flex-end;
}

.form_field {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.test-cases-container {
  & label,
  & input {
    display: inline-block;
    margin: 0;
    padding: 0;
    font-size: 1rem;
  }
}

input {
  padding: 1rem;
}

.test_case_file_uploads {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 1.5rem;
}

.file_upload {
  display: grid;
  gap: 0.25rem;
}

.file_id {
  font-size: 0.9rem;
}

strong {
  color: #555;
}
span.link {
  color: #555;
}
span.link:hover {
  color: #111;
  text-decoration: underline;
}
</style>
